'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Play, X } from 'lucide-react';
import { zIndex } from '@/shared/ui/z-index';
import { sendRecordedBroadcast, type BroadcastCamera, type RecordedBroadcastResult } from './recorded-broadcast-delivery';
import { sendSystemBroadcast, type SystemBroadcastRequest, type SystemBroadcastRunResult } from './system-broadcast-delivery';
import styles from './RecordedBroadcastProvider.module.css';

type BroadcastInput = { blob: Blob; durationMs: number; cameras: BroadcastCamera[]; canTransmit: () => boolean };
type SystemBroadcastInput = { label: string; requests: SystemBroadcastRequest[]; canTransmit: () => boolean };
type BroadcastContextValue = {
  sending: boolean;
  broadcast: (input: BroadcastInput) => Promise<RecordedBroadcastResult[]>;
  broadcastSystem: (input: SystemBroadcastInput) => Promise<SystemBroadcastRunResult>;
};
const BroadcastContext = createContext<BroadcastContextValue | null>(null);
const RESULT_VISIBLE_MS = 4_700;
const CLOSE_ANIMATION_MS = 240;
const TERMINAL_RESULT_STATUSES = new Set<RecordedBroadcastResult['status']>([
  'cancelled',
  'completed',
  'failed',
  'indeterminate',
  'queued',
  'sent',
  'skipped',
  'stopped',
  'unavailable',
  'unknown',
]);

export function useRecordedBroadcast() {
  const context = useContext(BroadcastContext);
  if (!context) throw new Error('RecordedBroadcastProvider가 필요합니다.');
  return context;
}

/** 위젯을 닫아도 전송과 재생 알림을 유지하는 전역 방송 상태입니다. */
export default function RecordedBroadcastProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [targets, setTargets] = useState<BroadcastCamera[]>([]);
  const [results, setResults] = useState<RecordedBroadcastResult[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [broadcastMode, setBroadcastMode] = useState<'recording' | 'system'>('recording');
  const [broadcastLabel, setBroadcastLabel] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const sendingRef = useRef(false);
  const autoCloseTimerRef = useRef<number | null>(null);
  const closeAnimationTimerRef = useRef<number | null>(null);

  const releaseAudio = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onended = null;
      audio.ontimeupdate = null;
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
  }, []);

  const clearCloseTimers = useCallback(() => {
    if (autoCloseTimerRef.current !== null) window.clearTimeout(autoCloseTimerRef.current);
    if (closeAnimationTimerRef.current !== null) window.clearTimeout(closeAnimationTimerRef.current);
    autoCloseTimerRef.current = null;
    closeAnimationTimerRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    clearCloseTimers();
    setClosing(true);
    closeAnimationTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      setClosing(false);
      releaseAudio();
      closeAnimationTimerRef.current = null;
    }, CLOSE_ANIMATION_MS);
  }, [clearCloseTimers, releaseAudio]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      clearCloseTimers();
      releaseAudio();
    },
    [clearCloseTimers, releaseAudio]
  );

  const broadcast = useCallback(
    async ({ blob, durationMs: nextDuration, cameras, canTransmit }: BroadcastInput) => {
      if (sendingRef.current || !cameras.length || !canTransmit()) return [];
      sendingRef.current = true;
      const generation = ++generationRef.current;
      clearCloseTimers();
      releaseAudio();
      setVisible(true);
      setClosing(false);
      setBroadcastMode('recording');
      setBroadcastLabel('');
      setSending(true);
      setChecking(true);
      setTargets(cameras);
      setResults([]);
      setElapsedMs(0);
      setDurationMs(nextDuration);
      setPlaybackError(null);
      try {
        return await sendRecordedBroadcast(
          blob,
          cameras,
          result => {
            setChecking(false);
            setResults(current => [...current.filter(item => item.uid !== result.uid), result]);
          },
          () => generationRef.current !== generation || !canTransmit(),
          availableCameras => {
            if (!availableCameras.length || generationRef.current !== generation) return;
            try {
              const url = URL.createObjectURL(blob);
              objectUrlRef.current = url;
              const audio = new Audio(url);
              audioRef.current = audio;
              audio.ontimeupdate = () => setElapsedMs(audio.currentTime * 1000);
              audio.onloadedmetadata = () => {
                if (Number.isFinite(audio.duration)) setDurationMs(audio.duration * 1000);
              };
              audio.onended = () => setElapsedMs(Number.isFinite(audio.duration) ? audio.duration * 1000 : nextDuration);
              audio.onerror = () => setPlaybackError('녹음본을 재생하지 못했습니다. 다시 재생해 주세요.');
              void audio.play().catch(() => {
                if (audioRef.current === audio) setPlaybackError('자동 재생이 차단되었습니다. 재생 버튼을 눌러 주세요.');
              });
            } catch {
              setPlaybackError('이 브라우저에서 녹음본을 재생하지 못했습니다.');
            }
          }
        );
      } finally {
        if (generationRef.current === generation) {
          setChecking(false);
          sendingRef.current = false;
          setSending(false);
        }
      }
    },
    [clearCloseTimers, releaseAudio]
  );

  const broadcastSystem = useCallback(
    async ({ label, requests, canTransmit }: SystemBroadcastInput): Promise<SystemBroadcastRunResult> => {
      if (sendingRef.current || !requests.length || !canTransmit()) return { responses: [], failures: [{ targetName: label, message: '다른 방송 요청이 진행 중입니다.' }] };
      sendingRef.current = true;
      const generation = ++generationRef.current;
      clearCloseTimers();
      releaseAudio();
      setVisible(false);
      setClosing(false);
      setSending(true);
      setChecking(false);
      setBroadcastMode('system');
      setBroadcastLabel(label);
      setTargets([]);
      setResults([]);
      setPlaybackError(null);
      try {
        return await sendSystemBroadcast(
          requests,
          nextTargets => {
            if (nextTargets.length > 0) setVisible(true);
            setTargets(nextTargets.map(({ uid, name }) => ({ uid, name })));
            setResults(nextTargets);
          },
          result => setResults(current => [...current.filter(item => item.uid !== result.uid), result]),
          () => generationRef.current !== generation || !canTransmit()
        );
      } finally {
        if (generationRef.current === generation) {
          sendingRef.current = false;
          setSending(false);
        }
      }
    },
    [clearCloseTimers, releaseAudio]
  );

  useEffect(() => {
    if (autoCloseTimerRef.current !== null) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (!visible || closing || sending || checking) return;
    const resultByTarget = new Map(results.map(result => [result.uid, result]));
    if (targets.length > 0 && !targets.every(target => TERMINAL_RESULT_STATUSES.has(resultByTarget.get(target.uid)?.status ?? 'available'))) return;
    autoCloseTimerRef.current = window.setTimeout(dismiss, RESULT_VISIBLE_MS);
    return () => {
      if (autoCloseTimerRef.current !== null) window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    };
  }, [checking, closing, dismiss, results, sending, targets, visible]);

  const total = targets.length;
  const completed = results.filter(result => result.status === 'completed').length;
  const failed = results.filter(result => result.status === 'failed').length;
  const available = results.filter(result => result.status === 'available').length;
  const preparing = results.filter(result => result.status === 'preparing').length;
  const unavailable = results.filter(result => result.status === 'unavailable').length;
  const unknown = results.filter(result => result.status === 'unknown' || result.status === 'indeterminate').length;
  const queued = results.filter(result => result.status === 'queued').length;
  const sent = results.filter(result => result.status === 'sent').length;
  const skipped = results.filter(result => result.status === 'skipped' || result.status === 'cancelled' || result.status === 'stopped').length;
  const active = results.filter(result => ['accepted', 'processing', 'streaming'].includes(result.status)).length;
  const progress = Math.min(100, Math.max(0, (elapsedMs / Math.max(1, durationMs)) * 100));
  const title =
    broadcastMode === 'system'
      ? sending
        ? `${broadcastLabel} 방송 상태 확인 중`
        : `${broadcastLabel} 방송 결과`
      : checking
        ? '전송 상태 확인 중'
        : sending
          ? '녹음 알림 전파중'
          : completed === total
            ? '녹음 알림 전파 완료'
            : '녹음 알림 전파 결과';
  const summary = checking
    ? `${total}대 방송 가능 여부 확인 중...`
    : [
        available ? `${available}대 전송 가능` : '',
        preparing ? `${preparing}대 변환 준비 중` : '',
        completed ? `${completed}대 전송 완료` : '',
        queued ? `${queued}건 IoT 큐 등록` : '',
        sent ? `${sent}건 IoT 전송` : '',
        active ? `${active}대 진행 중` : '',
        unavailable ? `${unavailable}대 전송 불가` : '',
        failed ? `${failed}대 실패` : '',
        unknown ? `${unknown}대 확인 불가` : '',
        skipped ? `${skipped}건 중지·제외` : '',
      ]
        .filter(Boolean)
        .join(' · ');
  const resultByCamera = new Map(results.map(result => [result.uid, result]));

  return (
    <BroadcastContext.Provider value={{ sending, broadcast, broadcastSystem }}>
      {children}
      {visible
        ? createPortal(
            <aside
              data-camera-broadcast-notification
              className={`${styles.popup} ${closing ? styles.popupClosing : ''}`}
              style={{ zIndex: zIndex.realtimeNotification }}
              aria-label={broadcastMode === 'recording' ? '녹음 알림 전파 상태' : '시스템 알림 전파 상태'}>
              <div className={styles.heading}>
                <div className={styles.titleRow}>
                  <h2>{title}</h2>
                  <button
                    type="button"
                    className={styles.close}
                    onClick={dismiss}
                    aria-label={broadcastMode === 'recording' ? '녹음 알림 닫기 및 이 PC 재생 정지' : '시스템 알림 닫기'}>
                    <X size={20} aria-hidden="true" />
                  </button>
                </div>
                <p role="status" title={results.map(result => `${result.name}: ${result.message}`).join('\n')}>
                  {summary}
                </p>
              </div>
              <ul className={styles.statusList} aria-label="CCTV별 전송 상태">
                {targets.map(camera => {
                  const result = resultByCamera.get(camera.uid);
                  const status = checking && !result ? 'checking' : (result?.status ?? 'available');
                  return (
                    <li key={camera.uid}>
                      <span className={styles.cameraName}>{camera.name}</span>
                      <span className={styles.cameraStatus} data-status={status} title={result?.message}>
                        {formatStatus(status, broadcastMode)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {broadcastMode === 'recording' ? (
                <div className={styles.player}>
                  <div
                    className={styles.track}
                    role="progressbar"
                    aria-label="전파 녹음본 재생"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress)}
                    aria-valuetext={`${formatTime(elapsedMs)} / ${formatTime(durationMs)}`}>
                    <div className={styles.played} style={{ width: `${progress}%` }} />
                    <span className={styles.cursor} style={{ left: `${progress}%` }} />
                  </div>
                  <div className={styles.times}>
                    <span>{formatTime(elapsedMs)}</span>
                    <span>{formatTime(durationMs)}</span>
                  </div>
                </div>
              ) : null}
              {broadcastMode === 'recording' && playbackError ? (
                <div className={styles.error} role="status">
                  <span>{playbackError}</span>
                  {audioRef.current ? (
                    <button
                      type="button"
                      onClick={() => {
                        const audio = audioRef.current;
                        if (!audio) return;
                        void audio
                          .play()
                          .then(() => setPlaybackError(null))
                          .catch(() => setPlaybackError('녹음본을 재생하지 못했습니다.'));
                      }}>
                      <Play size={16} aria-hidden="true" />
                      재생
                    </button>
                  ) : null}
                </div>
              ) : null}
            </aside>,
            document.body
          )
        : null}
    </BroadcastContext.Provider>
  );
}

function formatTime(ms: number) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map(value => String(value).padStart(2, '0')).join(':');
}

function formatStatus(status: RecordedBroadcastResult['status'] | 'checking', mode: 'recording' | 'system') {
  if (status === 'checking') return '확인 중';
  if (status === 'available') return '가능';
  if (status === 'unavailable') return '불가';
  if (status === 'indeterminate' || status === 'unknown') return '확인 불가';
  if (status === 'preparing') return mode === 'system' ? '요청 중' : '변환 준비';
  if (status === 'accepted' || status === 'processing') return mode === 'system' ? '재생 요청함' : status === 'accepted' ? '접수' : '처리 중';
  if (status === 'streaming') return mode === 'system' ? '재생 중' : '전파 중';
  if (status === 'completed') return mode === 'system' ? '재생 끝' : '완료';
  if (status === 'queued') return 'IoT 큐 등록';
  if (status === 'sent') return 'IoT 전송';
  if (status === 'skipped') return '제외';
  if (status === 'stopped') return '중지';
  if (status === 'cancelled') return '취소';
  return mode === 'system' ? '재생 실패' : '실패';
}
